import { makeAutoObservable, runInAction } from "mobx";
import { DataTableStore } from "./DataTableStore";
import { FiltersStore } from "./FiltersStore";
import { SelectFilterItem } from "@/components/filters";
import { OrderStatus } from "@/types/order/order.common";
import { ORDER_STATUS_LABELS } from "@/utils/orderHelpers";
import DateRangeFilter from "@/components/filters/DateRangeFilter";
import SearchFilter from "@/components/filters/SearchFilter";
import { companyService } from "@/services/CompanyService";
import { onB2BError } from "@/utils/b2bErrors";
import { B2B_ENABLED } from "@/utils/featureFlags";
/** Интервал авто-обновления (мс) при наличии заказов в статусе CHARGING */
const CHARGING_REFRESH_INTERVAL_MS = 30000;
/** FR-007: компании для фильтра «Плательщик» — заведомо больше, чем бывает у сервиса. */
const PAYER_COMPANIES_LIMIT = 500;
export class RefactoredOrderStore {
    constructor(orderService) {
        this.orderService = orderService;
        this.loading = false;
        /** FR-007 (UC-101): компании для режима «конкретная компания»; архивные остаются — по ним есть история. */
        this.payerCompanies = [];
        this.payerCompaniesLoaded = false;
        this._refreshIntervalId = null;
        this.setLoading = (loading) => {
            this.loading = loading;
        };
        this.initializeOrderFilters = () => {
            const filterItems = [
                // Status Filter
                {
                    id: "status-filter",
                    Cmp: SelectFilterItem,
                    props: {
                        name: "status",
                        label: "Статус заказа",
                        placeholder: "Выберите статусы",
                        allowClear: true,
                        options: Object.values(OrderStatus).map((status) => ({
                            label: ORDER_STATUS_LABELS[status],
                            value: status,
                        })),
                        onChange: (value) => this.filtersStore.updateFilter("status", value),
                    },
                    visible: true,
                    order: 1,
                },
                // Created Date Filter
                {
                    id: "created-date-filter",
                    Cmp: DateRangeFilter,
                    props: {
                        name: "createdDate",
                        label: "Дата создания",
                        placeholder: ["Дата от", "Дата до"],
                        allowClear: true,
                        onChange: (value) => {
                            if (Object.values(value).filter(Boolean).length === 0) {
                                this.filtersStore.clearFilter("createdDate");
                            }
                            else {
                                this.filtersStore.updateFilter("createdDate", value);
                            }
                        },
                    },
                    visible: true,
                    order: 2,
                },
                // Scheduled Date Filter
                {
                    id: "scheduled-date-filter",
                    Cmp: DateRangeFilter,
                    props: {
                        name: "scheduledDate",
                        label: "Плановая дата",
                        placeholder: ["Дата от", "Дата до"],
                        allowClear: true,
                        onChange: (value) => {
                            if (Object.values(value).filter(Boolean).length === 0) {
                                this.filtersStore.clearFilter("scheduledDate");
                            }
                            else {
                                this.filtersStore.updateFilter("scheduledDate", value);
                            }
                        },
                    },
                    visible: true,
                    order: 3,
                },
                // isPaid Filter — одиночный выбор, значения boolean
                {
                    id: "is-paid-filter",
                    Cmp: SelectFilterItem,
                    props: {
                        name: "isPaid",
                        label: "Оплата",
                        placeholder: "Статус оплаты",
                        allowClear: true,
                        mode: "single",
                        options: [
                            { label: "Оплачен", value: true },
                            { label: "Не оплачен", value: false },
                        ],
                        onChange: (value) => {
                            // Single-select clear отправляет undefined, выбор — boolean
                            this.filtersStore.updateFilter("isPaid", value === undefined || value === null ? undefined : Boolean(value));
                        },
                    },
                    visible: true,
                    order: 4,
                },
                // FR-007 (UC-101): «Плательщик» — четыре режима в одном списке: пусто = все, физлица, все корпоративные,
                // конкретная компания (значение — её id). Только при REACT_APP_B2B_ENABLED (BR-UI-01).
                ...(B2B_ENABLED
                    ? [
                        {
                            id: "payer-filter",
                            Cmp: SelectFilterItem,
                            props: {
                                name: "payer",
                                label: "Плательщик",
                                placeholder: "Все плательщики",
                                allowClear: true,
                                mode: "single",
                                testId: "corp-orders-filter-payer",
                                options: [
                                    { label: "Физлица", value: "PRIVATE" },
                                    { label: "Все корпоративные", value: "COMPANY" },
                                    {
                                        label: "Компания",
                                        title: "Компания",
                                        options: this.payerCompanies.map((company) => ({ label: company.name, value: company.id })),
                                    },
                                ],
                                onChange: (value) => this.filtersStore.updateFilter("payer", value ?? undefined),
                            },
                            visible: true,
                            order: 5,
                        },
                    ]
                    : []),
                // Search Filter
                {
                    id: "search-filter",
                    Cmp: SearchFilter,
                    props: {
                        name: "search",
                        label: "Поиск",
                        placeholder: "Поиск по номеру, адресу, клиенту, авто...",
                        allowClear: true,
                        showSearchIcon: true,
                        onChange: (value) => this.filtersStore.updateFilter("search", value),
                    },
                    visible: true,
                    order: 6,
                },
            ];
            this.filtersStore.setFilterItems(filterItems);
        };
        makeAutoObservable(this);
        this.dataTable = new DataTableStore({
            defaultFilters: {
                status: [],
                createdDate: { dateFrom: "", dateTo: "" },
                scheduledDate: { dateFrom: "", dateTo: "" },
                search: "",
                isPaid: undefined,
                payer: undefined,
            },
            dataLoader: {
                loadData: (params) => this.loadOrders(params),
            },
        });
        this.filtersStore = new FiltersStore();
        this.initializeOrderFilters();
    }
    async loadOrders(params) {
        try {
            const result = await this.orderService.getOrdersWithParams({
                page: params.page,
                pageSize: params.pageSize,
                filters: params.filters,
                sorters: params.sorters,
            });
            // Управляем авто-обновлением: запускаем если есть CHARGING-заказы
            this.updateAutoRefresh(result.data);
            return {
                data: result.data,
                total: result.total,
                page: params.page,
                pageSize: params.pageSize,
            };
        }
        catch (error) {
            throw error;
        }
    }
    async deleteOrder(id) {
        this.setLoading(true);
        try {
            await this.orderService.deleteOrder(id);
            await this.dataTable.refresh();
        }
        catch (error) {
            // FR-007 (UC-107): CP013 — текстом из карты, без сырого текста Postgres
            onB2BError(error, "Не удалось удалить заказ");
        }
        finally {
            this.setLoading(false);
        }
    }
    /**
     * FR-007: список компаний для фильтра «Плательщик». Только при `B2B_ENABLED` — без флага `v_corp_companies`
     * не запрашивается (BR-UI-01). Ошибка не мешает списку: остаются режимы «физлица» и «все корпоративные».
     */
    async loadPayerCompanies() {
        if (!B2B_ENABLED || this.payerCompaniesLoaded)
            return;
        this.payerCompaniesLoaded = true;
        try {
            const result = await companyService.getCompanies({
                page: 1,
                pageSize: PAYER_COMPANIES_LIMIT,
                sorters: { name: "ascend" },
            });
            runInAction(() => {
                this.payerCompanies = result.data.map((company) => ({ id: company.id, name: company.name }));
                this.initializeOrderFilters();
            });
        }
        catch (error) {
            runInAction(() => {
                this.payerCompaniesLoaded = false;
            });
            onB2BError(error, "Не удалось загрузить компании для фильтра «Плательщик»");
        }
    }
    /** Вызывается при размонтировании страницы для очистки таймера */
    dispose() {
        this.stopAutoRefresh();
    }
    // === Авто-обновление ===
    updateAutoRefresh(orders) {
        const hasCharging = orders.some((o) => o.status === OrderStatus.CHARGING);
        if (hasCharging) {
            this.startAutoRefresh();
        }
        else {
            this.stopAutoRefresh();
        }
    }
    startAutoRefresh() {
        if (this._refreshIntervalId !== null)
            return; // уже запущен
        this._refreshIntervalId = setInterval(() => {
            this.dataTable.refresh();
        }, CHARGING_REFRESH_INTERVAL_MS);
    }
    stopAutoRefresh() {
        if (this._refreshIntervalId !== null) {
            clearInterval(this._refreshIntervalId);
            this._refreshIntervalId = null;
        }
    }
}
